import { apiHelpers } from '@/services/api';

// Feature Request Status - must match backend enum
export type FeatureRequestStatus = 'OPEN' | 'IN_PROGRESS' | 'COMPLETED' | 'REJECTED';

// Feature Request Category - must match backend enum
export type FeatureRequestCategory = 'UI_UX' | 'NEW_FEATURE' | 'IMPROVEMENT' | 'BUG_FIX' | 'PERFORMANCE' | 'OTHER';

// User profile info
export interface UserProfile {
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
}

// User with profile
export interface UserWithProfile {
  id: string;
  email: string;
  profile: UserProfile | null;
}

// Feature Request with full details
export interface FeatureRequest {
  id: string;
  title: string;
  description: string;
  category: FeatureRequestCategory;
  status: FeatureRequestStatus;
  priority: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  creator: UserWithProfile;
  _count: {
    votes: number;
    comments: number;
  };
  hasVoted: boolean;
}

// Feature Request Comment
export interface FeatureRequestComment {
  id: string;
  requestId: string;
  userId: string;
  comment: string;
  createdAt: string;
  user: UserWithProfile;
}

// Feature Request Filters for list
export interface FeatureRequestFilters {
  page?: number;
  limit?: number;
  sortBy?: 'votes' | 'date' | 'priority';
  sortOrder?: 'asc' | 'desc';
  status?: FeatureRequestStatus;
  category?: FeatureRequestCategory;
  createdBy?: string;
  search?: string;
}

// Create Feature Request Input
export interface CreateFeatureRequestInput {
  title: string;
  description: string;
  category?: FeatureRequestCategory;
}

// Update Feature Request Input
export interface UpdateFeatureRequestInput {
  title?: string;
  description?: string;
  category?: FeatureRequestCategory;
}

// Update Status Input (Admin only)
export interface UpdateStatusInput {
  status: FeatureRequestStatus;
  priority?: number;
}

// Add Comment Input
export interface AddCommentInput {
  comment: string;
}

// Pagination Meta
export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

// Paginated Response
export interface PaginatedFeatureRequestsResponse {
  featureRequests: FeatureRequest[];
  meta: PaginationMeta;
}

// Comments Paginated Response
export interface PaginatedCommentsResponse {
  comments: FeatureRequestComment[];
  meta: PaginationMeta;
}

// Feature Request Statistics
export interface FeatureRequestStats {
  totalRequests: number;
  openRequests: number;
  inProgressRequests: number;
  completedRequests: number;
  rejectedRequests: number;
  myRequests: number;
  myVotes: number;
}

// Feature Request Service
export const featureRequestService = {
  /**
   * Get list of feature requests with filters
   */
  async getFeatureRequests(filters?: FeatureRequestFilters): Promise<PaginatedFeatureRequestsResponse> {
    const params: Record<string, unknown> = {};

    if (filters) {
      if (filters.page) params.page = filters.page;
      if (filters.limit) params.limit = filters.limit;
      if (filters.sortBy) params.sortBy = filters.sortBy;
      if (filters.sortOrder) params.sortOrder = filters.sortOrder;
      if (filters.status) params.status = filters.status;
      if (filters.category) params.category = filters.category;
      if (filters.createdBy) params.createdBy = filters.createdBy;
      if (filters.search) params.search = filters.search;
    }

    return apiHelpers.get<PaginatedFeatureRequestsResponse>('/feature-requests', params);
  },

  /**
   * Get a single feature request by ID
   */
  async getFeatureRequestById(id: string): Promise<FeatureRequest> {
    return apiHelpers.get<FeatureRequest>(`/feature-requests/${id}`);
  },

  /**
   * Create a new feature request
   */
  async createFeatureRequest(data: CreateFeatureRequestInput): Promise<FeatureRequest> {
    return apiHelpers.post<FeatureRequest>('/feature-requests', data);
  },

  /**
   * Update a feature request (only creator, only if status is OPEN)
   */
  async updateFeatureRequest(id: string, data: UpdateFeatureRequestInput): Promise<FeatureRequest> {
    return apiHelpers.put<FeatureRequest>(`/feature-requests/${id}`, data);
  },

  /**
   * Delete a feature request (only creator, only if status is OPEN)
   */
  async deleteFeatureRequest(id: string): Promise<void> {
    return apiHelpers.delete(`/feature-requests/${id}`);
  },

  /**
   * Update feature request status (Admin only)
   */
  async updateStatus(id: string, data: UpdateStatusInput): Promise<FeatureRequest> {
    return apiHelpers.patch<FeatureRequest>(`/feature-requests/${id}/status`, data);
  },

  /**
   * Vote on a feature request
   */
  async vote(id: string): Promise<FeatureRequest> {
    return apiHelpers.post<FeatureRequest>(`/feature-requests/${id}/vote`);
  },

  /**
   * Remove vote from a feature request
   */
  async removeVote(id: string): Promise<FeatureRequest> {
    return apiHelpers.delete<FeatureRequest>(`/feature-requests/${id}/vote`);
  },

  /**
   * Add a comment to a feature request
   */
  async addComment(id: string, data: AddCommentInput): Promise<FeatureRequestComment> {
    return apiHelpers.post<FeatureRequestComment>(`/feature-requests/${id}/comments`, data);
  },

  /**
   * Get comments for a feature request
   */
  async getComments(id: string, page = 1, limit = 20): Promise<PaginatedCommentsResponse> {
    return apiHelpers.get<PaginatedCommentsResponse>(`/feature-requests/${id}/comments`, { page, limit });
  },

  /**
   * Delete a comment (only comment creator)
   */
  async deleteComment(requestId: string, commentId: string): Promise<void> {
    return apiHelpers.delete(`/feature-requests/${requestId}/comments/${commentId}`);
  },

  /**
   * Get feature request statistics
   */
  async getStats(): Promise<FeatureRequestStats> {
    return apiHelpers.get<FeatureRequestStats>('/feature-requests/stats');
  },
};

export default featureRequestService;
